import React, { Component } from "react";
import {
  withStyles,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Button,
  Divider,
} from "@material-ui/core";
import { CheckCircle, Error, OpenInNew } from "@material-ui/icons";
import axios from "axios";
import { withRouter } from "react-router-dom";
import { INTEGRATION_SERVER_URL, RAZORPAY_CLIENT_ID } from "../../config/config";

const RAZORPAY_LOGO = "https://aisensy-integrations-icons.s3.ap-south-1.amazonaws.com/razorpay/razorpay_icon.png";

class RazorpayPartnerAuth extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      isSubmitting: false,
      validated: false,
      data: {},
      errMsg: "",
      success: false,
    };
  }

  componentDidMount() {
    this.handleRouting();
  }

  handleRouting = async () => {
    const { match, location } = this.props;
    const { key } = match.params;
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (key) {
      window.localStorage.setItem("rzpy-partner-key", key);
      await this.verifyLink(key);
    } else if (code) {
      const savedKey = window.localStorage.getItem("rzpy-partner-key");
      if (savedKey) {
        await this.connectRazorpay(code, state, savedKey);
      } else {
        this.setState({
          isLoading: false,
          errMsg: "Session expired or invalid link. Please try again from your dashboard.",
        });
      }
    } else {
      this.setState({
        isLoading: false,
        errMsg: "Invalid access. Link parameters missing.",
      });
    }
  };

  verifyLink = async (key) => {
    try {
      const response = await axios.post(`${INTEGRATION_SERVER_URL}/razorpay/t1/verify-razorpay-link`, { key });
      this.setState({
        validated: true,
        data: response.data,
        isLoading: false,
      });
    } catch (error) {
      console.error(error);
      this.setState({
        isLoading: false,
        errMsg: error.response?.data?.message || "Unauthorized or invalid link.",
      });
    }
  };

  connectRazorpay = async (code, state, key) => {
    this.setState({ isSubmitting: true, isLoading: false });
    try {
      await axios.post(`${INTEGRATION_SERVER_URL}/razorpay/t1/connect-razorpay-link`, {
        code,
        state,
        key,
        domain: window.location.origin,
      });

      this.setState({ success: true, isSubmitting: false });

      let finalUrl = this.state.data?.finalRedirectUrl;
      if (!finalUrl) {
        try {
          const verifyRes = await axios.post(`${INTEGRATION_SERVER_URL}/razorpay/t1/verify-razorpay-link`, { key });
          finalUrl = verifyRes.data.finalRedirectUrl;
        } catch (e) {
          finalUrl = "/";
        }
      }

      setTimeout(() => {
        window.location.replace(finalUrl);
      }, 2000);

      window.localStorage.removeItem("rzpy-partner-key");
    } catch (error) {
      console.error(error);
      this.setState({
        isSubmitting: false,
        errMsg: error.response?.data?.message || "Failed to connect Razorpay account.",
      });
    }
  };

  authenticateRazorpay = () => {
    const { data } = this.state;
    const { assistantId } = data;
    const callBackUrl = `${window.location.origin}/rzp/auth`;
    const clientId = data?.razorpayClientId || RAZORPAY_CLIENT_ID;
    const authorizationUrl = `https://auth.razorpay.com/authorize?response_type=code&client_id=${clientId}&redirect_uri=${callBackUrl}&scope=read_write&state=${assistantId}`;
    window.location.href = authorizationUrl;
  };

  handleErrorRedirect = async () => {
    const key = window.localStorage.getItem("rzpy-partner-key");
    let finalUrl = this.state.data?.finalRedirectUrl || "/";

    if (!this.state.data?.finalRedirectUrl && key) {
      try {
        const verifyRes = await axios.post(`${INTEGRATION_SERVER_URL}/razorpay/t1/verify-razorpay-link`, { key });
        finalUrl = verifyRes.data.finalRedirectUrl;
      } catch (e) {}
    }

    window.location.replace(finalUrl);
  };

  render() {
    const { classes } = this.props;
    const { isLoading, isSubmitting, validated, data, errMsg, success } = this.state;

    if (isLoading) {
      return (
        <div className={classes.fullPage}>
          <CircularProgress style={{ color: "#3395ff" }} size={40} />
          <Typography variant="body2" style={{ marginTop: 16, color: "#666" }}>
            Verifying link...
          </Typography>
        </div>
      );
    }

    if (errMsg) {
      return (
        <div className={classes.fullPage}>
          <Box className={classes.stateCard}>
            <Error style={{ color: "#e53935", fontSize: 56, marginBottom: 16 }} />
            <Typography variant="h6" style={{ fontWeight: 700, color: "#1a1a1a", marginBottom: 8 }}>
              Something went wrong
            </Typography>
            <Typography variant="body2" style={{ color: "#666", marginBottom: 28, maxWidth: 320, textAlign: "center" }}>
              {errMsg}
            </Typography>
            <Button
              variant="contained"
              onClick={this.handleErrorRedirect}
              className={classes.primaryBtn}
            >
              Go Back to Dashboard
            </Button>
          </Box>
        </div>
      );
    }

    if (isSubmitting) {
      return (
        <div className={classes.fullPage}>
          <CircularProgress style={{ color: "#3395ff" }} size={40} />
          <Typography variant="body1" style={{ marginTop: 16, fontWeight: 600, color: "#1a1a1a" }}>
            Connecting your account...
          </Typography>
          <Typography variant="body2" style={{ marginTop: 6, color: "#888" }}>
            Please don't refresh or close this tab.
          </Typography>
        </div>
      );
    }

    if (success) {
      return (
        <div className={classes.fullPage}>
          <Box className={classes.stateCard}>
            <Box className={classes.successIcon}>
              <CheckCircle style={{ color: "#fff", fontSize: 32 }} />
            </Box>
            <Typography variant="h6" style={{ fontWeight: 700, color: "#1a1a1a", marginBottom: 8, marginTop: 20 }}>
              Successfully Connected!
            </Typography>
            <Typography variant="body2" style={{ color: "#666" }}>
              Redirecting you back to your dashboard...
            </Typography>
          </Box>
        </div>
      );
    }

    if (validated) {
      const { partnerName, assistantName, businessName } = data;
      return (
        <div className={classes.root}>
          <Grid container justify="center" alignItems="center" style={{ minHeight: "100vh", padding: "24px 16px" }}>
            <Grid item xs={12} sm={10} md={6} lg={4}>
              <Box className={classes.card}>

                {/* Logo area */}
                <Box className={classes.logoArea}>
                  <Box className={classes.logoBadge}>
                    <img src={RAZORPAY_LOGO} alt="Razorpay" style={{ width: 36, height: 36, objectFit: "contain" }} />
                  </Box>
                </Box>

                {/* Title */}
                <Typography variant="h5" align="center" className={classes.title}>
                  Connect Razorpay
                </Typography>
                <Typography variant="body2" align="center" className={classes.subtitle}>
                  Authorize <span className={classes.highlight}>{partnerName}</span> to receive payment
                  events from your Razorpay account.
                </Typography>

                <Divider style={{ margin: "24px 0" }} />

                {/* Connection Details */}
                <Box className={classes.detailsBox}>
                  <Typography variant="overline" className={classes.detailsLabel}>
                    Connection Details
                  </Typography>
                  <Box className={classes.detailRow}>
                    <Typography variant="body2" className={classes.detailKey}>Business</Typography>
                    <Typography variant="body2" className={classes.detailValue}>{businessName}</Typography>
                  </Box>
                  <Box className={classes.detailRow}>
                    <Typography variant="body2" className={classes.detailKey}>Project</Typography>
                    <Typography variant="body2" className={classes.detailValue}>{assistantName}</Typography>
                  </Box>
                </Box>

                {/* Permissions */}
                <Box className={classes.permissionsBox}>
                  <Typography variant="overline" className={classes.detailsLabel}>
                    This will allow
                  </Typography>
                  {["Receive payment event notifications", "Manage Razorpay webhooks", "Read transaction data"].map((perm) => (
                    <Box key={perm} display="flex" alignItems="center" mt={1}>
                      <CheckCircle style={{ color: "#27c152", fontSize: 16, marginRight: 8, flexShrink: 0 }} />
                      <Typography variant="body2" style={{ color: "#444" }}>{perm}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* CTA */}
                <Button
                  fullWidth
                  variant="contained"
                  className={classes.connectButton}
                  onClick={this.authenticateRazorpay}
                  endIcon={<OpenInNew style={{ fontSize: 16 }} />}
                >
                  Authorize with Razorpay
                </Button>

                <Typography variant="caption" align="center" display="block" style={{ color: "#aaa", marginTop: 16, lineHeight: 1.5 }}>
                  You'll be redirected to Razorpay to complete authorization securely.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </div>
      );
    }

    return null;
  }
}

const styles = (theme) => ({
  root: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)",
    boxSizing: "border-box",
  },
  fullPage: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)",
    padding: 24,
  },
  stateCard: {
    background: "#fff",
    borderRadius: 20,
    padding: "48px 40px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
    maxWidth: 400,
    width: "100%",
    textAlign: "center",
  },
  successIcon: {
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #27c152, #1da844)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 8px 20px rgba(39,193,82,0.3)",
  },
  card: {
    background: "#fff",
    borderRadius: 20,
    padding: "40px 36px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
    [theme.breakpoints.down("xs")]: {
      padding: "32px 20px",
      borderRadius: 16,
    },
  },
  logoArea: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 24,
  },
  logoBadge: {
    width: 72,
    height: 72,
    borderRadius: 18,
    background: "#fff8f5",
    border: "2px solid #ffe5d8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
  },
  title: {
    fontWeight: 700,
    color: "#111",
    marginBottom: 8,
  },
  subtitle: {
    color: "#666",
    lineHeight: 1.6,
  },
  highlight: {
    fontWeight: 600,
    color: "#3395ff",
  },
  detailsBox: {
    background: "#f9fafb",
    borderRadius: 12,
    padding: "16px 20px",
    marginBottom: 16,
    border: "1px solid #efefef",
  },
  permissionsBox: {
    background: "#f6fdf8",
    borderRadius: 12,
    padding: "16px 20px",
    marginBottom: 28,
    border: "1px solid #d8f3e1",
  },
  detailsLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    color: "#999",
    display: "block",
    marginBottom: 10,
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
  },
  detailKey: {
    color: "#888",
  },
  detailValue: {
    fontWeight: 600,
    color: "#222",
    maxWidth: "55%",
    textAlign: "right",
    wordBreak: "break-word",
  },
  connectButton: {
    background: "linear-gradient(135deg, #3395ff 0%, #1a73e8 100%)",
    color: "#fff",
    fontWeight: 600,
    height: 52,
    borderRadius: 10,
    textTransform: "none",
    fontSize: 15,
    boxShadow: "0 4px 14px rgba(51,149,255,0.35)",
    "&:hover": {
      background: "linear-gradient(135deg, #1a73e8 0%, #1558b0 100%)",
      boxShadow: "0 6px 18px rgba(51,149,255,0.45)",
    },
  },
  primaryBtn: {
    background: "linear-gradient(135deg, #3395ff 0%, #1a73e8 100%)",
    color: "#fff",
    fontWeight: 600,
    height: 48,
    borderRadius: 10,
    textTransform: "none",
    fontSize: 14,
    padding: "0 28px",
    boxShadow: "0 4px 14px rgba(51,149,255,0.3)",
    "&:hover": {
      background: "linear-gradient(135deg, #1a73e8 0%, #1558b0 100%)",
    },
  },
});

export default withStyles(styles)(withRouter(RazorpayPartnerAuth));
